import { useEffect, useMemo, useState } from 'react';

import FavoriteIcon from '@mui/icons-material/Favorite';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { useUnfollowFic } from 'src/pages/Monitor/hooks/useUnfollowFic';
import {
  FicDashboardSummary,
  FicDashboardSummaryStatus,
  getFicDashboardSummary,
} from 'src/services/wallet/ficDashboardSummary';
import { followingFicDashboardSummaryQueryKey } from 'src/services/wallet/queryKeys';

import {
  filterAndSortFollowingFics,
  FollowingFicSort,
  formatDashboardCompetence,
  formatDashboardCount,
  formatDashboardFullMoney,
  formatDashboardMoney,
  formatDashboardPercentage,
} from './followingFicDashboardUtils';
import { useMonitorContext } from '../MonitorContextProvider';

const dashboardStatusLabels: Record<FicDashboardSummaryStatus, string> = {
  available: 'Disponível',
  partial: 'Parcial',
  empty: 'Vazio',
  unavailable: 'Indisponível',
  error: 'Erro',
};

const noCardsSummaryMessages: Record<
  Exclude<FicDashboardSummaryStatus, 'empty'>,
  string
> = {
  available: 'O resumo dos FICs seguidos não trouxe dados.',
  partial: 'O resumo dos FICs seguidos está parcial.',
  unavailable: 'O resumo dos FICs seguidos está indisponível.',
  error: 'Não foi possível carregar os FICs seguidos.',
};

const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute' as const,
  whiteSpace: 'nowrap' as const,
  width: '1px',
};

interface SummaryMetricProps {
  label: string;
  value: string;
  valueTitle?: string;
  valueAriaLabel?: string;
}

function SummaryMetric({
  label,
  value,
  valueTitle,
  valueAriaLabel,
}: SummaryMetricProps) {
  return (
    <Grid item xs={6}>
      <Box
        component="dl"
        m={0}
        p={0.75}
        border="1px solid"
        borderColor="grey.200"
        borderRadius={1.5}
        bgcolor="grey.50">
        <Typography component="dt" variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography
          component="dd"
          variant="subtitle1"
          fontWeight={700}
          m={0}
          title={valueTitle}
          aria-label={valueAriaLabel}>
          {value}
        </Typography>
      </Box>
    </Grid>
  );
}

function FollowingFicCard({ card }: { card: FicDashboardSummary }) {
  const { mutate: unfollow, isLoading: isUnfollowing } = useUnfollowFic();
  const detailsPath = card.fic.operationId
    ? `/monitor/wallet/following/${encodeURIComponent(card.fic.operationId)}`
    : undefined;
  const fullNetEquity = formatDashboardFullMoney(card.netEquity);
  const top5Percentage = card.top5.percentageOfFicNetEquity;
  const top5AlertCount = card.top5.alertFundCount;
  const cardStatusDescription = `Estado do FIC: ${dashboardStatusLabels[card.status]}. ${card.reason}`;
  const statusDescriptionId = `following-fic-${card.fic.id}-status`;

  return (
    <Card
      component="article"
      variant="outlined"
      aria-labelledby={`following-fic-${card.fic.id}-title`}
      aria-describedby={statusDescriptionId}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        borderColor: 'grey.300',
      }}>
      <CardContent sx={{ flexGrow: 1, p: 2, '&:last-child': { pb: 1.5 } }}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
          mb={1}>
          <Typography
            id={`following-fic-${card.fic.id}-title`}
            component="h2"
            variant="subtitle1"
            fontWeight={700}
            title={card.fic.name ?? 'FIC sem nome'}
            sx={{
              minWidth: 0,
              flexGrow: 1,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflowWrap: 'anywhere',
            }}>
            {card.fic.name || 'FIC sem nome'}
          </Typography>
          {card.fic.operationId ? (
            <Tooltip title="Parar de seguir este FIC" arrow>
              <span>
                <IconButton
                  aria-label={`Parar de seguir ${card.fic.name || 'este FIC'}`}
                  disabled={isUnfollowing}
                  onClick={() => unfollow(card.fic.operationId ?? '')}
                  size="small"
                  sx={{ color: '#E66A00', flexShrink: 0 }}>
                  {isUnfollowing ? (
                    <CircularProgress
                      size={18}
                      aria-label="Parando de seguir"
                    />
                  ) : (
                    <FavoriteIcon fontSize="small" aria-hidden="true" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <FavoriteIcon
              fontSize="small"
              aria-hidden="true"
              sx={{ color: '#E66A00', flexShrink: 0 }}
            />
          )}
        </Stack>

        <Grid container spacing={1} mb={1}>
          <SummaryMetric
            label="Número de FIDCs"
            value={formatDashboardCount(card.totalMonitoredFundsCount)}
          />
          <SummaryMetric
            label="Patrimônio Líquido"
            value={formatDashboardMoney(card.netEquity)}
            valueTitle={fullNetEquity}
            valueAriaLabel={`PL do FIC: ${fullNetEquity}`}
          />
        </Grid>

        <Box
          component="section"
          aria-labelledby={`following-fic-${card.fic.id}-top5-title`}
          sx={{
            mb: 1.5,
            px: 1.25,
            py: 1,
            borderRadius: 2,
            border: '1px solid #FFD0AD',
            borderLeft: '3px solid #E66A00',
            bgcolor: '#FFFCFA',
          }}>
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between">
            <Typography
              id={`following-fic-${card.fic.id}-top5-title`}
              component="h3"
              variant="caption"
              fontWeight={700}
              sx={{
                color: '#A64B00',
                px: 0.75,
                py: 0.25,
                borderRadius: 0.75,
                bgcolor: '#FFF0E5',
                fontSize: '0.625rem',
              }}>
              TOP 5 FIDCs
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              color="primary.main"
              aria-label={`TOP 5 como percentual do PL do FIC: ${formatDashboardPercentage(top5Percentage)}`}>
              {formatDashboardPercentage(top5Percentage)}
            </Typography>
          </Stack>
          {top5Percentage !== null && Number.isFinite(top5Percentage) ? (
            <LinearProgress
              aria-label={`TOP 5 representa ${formatDashboardPercentage(top5Percentage)} do PL do FIC`}
              variant="determinate"
              value={top5Percentage}
              sx={{
                mt: 0.75,
                height: 6,
                borderRadius: 4,
                bgcolor: '#F2E5DB',
                '& .MuiLinearProgress-bar': { borderRadius: 4 },
              }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary" role="status">
              Proporção do PL indisponível
            </Typography>
          )}
          <Box
            role="group"
            aria-label={
              top5AlertCount === null
                ? 'Quantidade de FIDCs dos TOP 5 com alertas: Indisponível'
                : `${formatDashboardCount(top5AlertCount)} ${top5AlertCount === 1 ? 'FIDC' : 'FIDCs'} dos TOP 5 com alertas`
            }
            sx={{
              mt: 0.75,
              px: 1,
              py: 0.25,
              border: '1px solid #FFE1BD',
              borderRadius: 1,
              bgcolor: '#FFF6E9',
              color: '#8A4B00',
              fontSize: '0.75rem',
              fontWeight: 600,
              lineHeight: 1.4,
            }}>
            {top5AlertCount === null
              ? 'Indisponível'
              : `${formatDashboardCount(top5AlertCount)} com alertas`}
          </Box>
        </Box>

        <Box
          component="section"
          aria-labelledby={`following-fic-${card.fic.id}-alerts-title`}
          sx={{
            px: 1,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: 'grey.50',
          }}>
          <Typography
            id={`following-fic-${card.fic.id}-alerts-title`}
            component="h3"
            variant="body2"
            fontWeight={700}
            mb={0.25}>
            Alertas do FIC
          </Typography>
          <Typography
            aria-label={`Escopo da distribuição: ${formatDashboardCount(card.persistedFidcCount)} FIDCs + 1 FIC`}
            color="text.secondary"
            display="block"
            variant="caption">
            {formatDashboardCount(card.persistedFidcCount)}{' '}
            {card.persistedFidcCount === 1 ? 'FIDC' : 'FIDCs'} + 1 FIC
          </Typography>
          <Grid container component="dl" spacing={0.5}>
            {[
              {
                label: 'Críticos',
                value: card.alertBreakdown.critical,
                color: 'error.main',
              },
              {
                label: 'Atenção',
                value: card.alertBreakdown.attention,
                color: 'warning.dark',
              },
              {
                label: 'Sem alerta',
                value: card.alertBreakdown.none,
                color: 'success.dark',
              },
            ].map(({ label, value, color }) => (
              <Grid item xs={4} key={label}>
                <Typography
                  component="dt"
                  variant="caption"
                  color="text.secondary">
                  {label}
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  fontWeight={700}
                  m={0}
                  color={color}>
                  {formatDashboardCount(value)}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
        <Typography id={statusDescriptionId} sx={visuallyHidden}>
          {cardStatusDescription}
        </Typography>
      </CardContent>
      <CardActions sx={{ px: 2, pt: 0, pb: 2 }}>
        {detailsPath ? (
          <Button
            component={RouterLink}
            to={{
              pathname: detailsPath,
              state: {
                operationMetadata: {
                  operation: {
                    hashId: card.fic.operationId,
                    name: card.fic.name,
                    cnpj: card.fic.cnpj ?? '',
                    slug: card.fic.slug ?? '',
                    market: null,
                  },
                },
              },
            }}
            size="small"
            fullWidth
            variant="outlined">
            Ver detalhes
          </Button>
        ) : (
          <Button size="small" fullWidth variant="outlined" disabled>
            Ver detalhes
          </Button>
        )}
      </CardActions>
    </Card>
  );
}

export function FollowingTab() {
  const { auth } = useAuthContext();
  const { indicatorsCompetencyDate, setWallet } = useMonitorContext();
  const userId = auth?.user?.id ?? null;
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<FollowingFicSort>('criticality');

  const {
    data: summary,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: followingFicDashboardSummaryQueryKey(
      userId,
      indicatorsCompetencyDate,
    ),
    queryFn: () => getFicDashboardSummary(indicatorsCompetencyDate),
    enabled: userId !== null,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    return () => setWallet(null);
  }, [setWallet]);

  const visibleCards = useMemo(
    () => filterAndSortFollowingFics(summary?.data ?? [], searchTerm, sortBy),
    [searchTerm, sortBy, summary?.data],
  );

  if (userId === null) {
    return (
      <Box role="alert" px={3} py={4}>
        <Typography variant="h6">Sessão necessária</Typography>
        <Typography color="text.secondary">
          Entre na plataforma para consultar os FICs seguidos.
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        role="status"
        aria-live="polite"
        minHeight={320}
        display="flex"
        alignItems="center"
        justifyContent="center">
        <CircularProgress aria-label="Carregando resumo dos FICs seguidos" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box px={3} py={3}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          }>
          Não foi possível carregar os FICs seguidos.
        </Alert>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box px={3} py={3}>
        <Alert severity="error">
          O resumo dos FICs seguidos está indisponível.
        </Alert>
      </Box>
    );
  }

  if (summary.data.length === 0 && summary.meta.status !== 'empty') {
    const summaryStatus = summary.meta.status;
    const isSummaryError = summaryStatus === 'error';

    return (
      <Box px={3} py={3}>
        <Alert
          severity={isSummaryError ? 'error' : 'warning'}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          }>
          {noCardsSummaryMessages[summaryStatus]}
          <Box component="span" sx={visuallyHidden}>
            Estado do resumo dos FICs seguidos:{' '}
            {dashboardStatusLabels[summary.meta.status]}. Motivo:{' '}
            {summary.meta.reason}
          </Box>
        </Alert>
      </Box>
    );
  }

  if (summary.meta.status === 'empty' && summary.data.length === 0) {
    return (
      <Box
        minHeight={320}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={1}
        px={3}
        textAlign="center">
        <Box role="status" aria-live="polite" sx={visuallyHidden}>
          Estado do resumo dos FICs seguidos:{' '}
          {dashboardStatusLabels[summary.meta.status]}. {summary.meta.reason}
        </Box>
        <Typography variant="h6" color="text.secondary">
          Você ainda não segue nenhum FIC
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ao seguir um FIC, o resumo dele aparecerá aqui.
        </Typography>
      </Box>
    );
  }

  const indicatorsCompetence = formatDashboardCompetence(
    summary.meta.competence,
  );

  return (
    <Box px={{ xs: 2, md: 3 }} py={2}>
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        Estado do resumo dos FICs seguidos:{' '}
        {dashboardStatusLabels[summary.meta.status]}. {summary.meta.reason}
      </Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        mb={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}>
        <TextField
          label="Buscar FIC"
          type="search"
          size="small"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          inputProps={{ 'aria-label': 'Buscar FIC seguido pelo nome' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon aria-hidden="true" />
              </InputAdornment>
            ),
          }}
          fullWidth
        />
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
          <InputLabel id="following-fic-sort-label">Ordenar por</InputLabel>
          <Select
            labelId="following-fic-sort-label"
            id="following-fic-sort"
            value={sortBy}
            label="Ordenar por"
            size="small"
            onChange={(event) =>
              setSortBy(event.target.value as FollowingFicSort)
            }>
            <MenuItem value="criticality">Mais críticos</MenuItem>
            <MenuItem value="fidc-count">Mais FIDCs</MenuItem>
            <MenuItem value="alphabetical">Ordem alfabética</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {indicatorsCompetence && (
        <Alert
          severity="info"
          role="note"
          aria-live="polite"
          sx={{ mb: 2, py: 0.25 }}>
          Dados dos FICs referentes à competência de{' '}
          <strong>{indicatorsCompetence}</strong>.
        </Alert>
      )}

      {visibleCards.length === 0 ? (
        <Box role="status" aria-live="polite" py={5} textAlign="center">
          <Typography color="text.secondary">
            Nenhum FIC corresponde à busca.
          </Typography>
        </Box>
      ) : (
        <Grid
          container
          spacing={2}
          role="list"
          aria-label="Cards de FICs seguidos">
          {visibleCards.map((card) => (
            <Grid item role="listitem" xs={12} sm={6} lg={4} key={card.fic.id}>
              <FollowingFicCard card={card} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
